import { Tooltip, useTheme } from '@mui/material';
import { useState } from 'react';
import { ActivityCountKey, DailyActivity } from '@/hooks/api/tenants/useGetTenantActivityMetrics';
import { formatDay } from './activitySeries';

const WIDTH = 260;
const HEIGHT = 72;
const MARGIN = { top: 6, right: 6, bottom: 16, left: 30 };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;
const AXIS_FONT_SIZE = 10;

const niceCeiling = (value: number) => {
  if (value <= 1) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 5, 10].find(candidate => value / magnitude <= candidate) ?? 10;
  return step * magnitude;
};

export const ActivitySparkline = ({
  metric,
  label,
  daily,
}: {
  metric: ActivityCountKey;
  label: string;
  daily: DailyActivity[];
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>();
  const theme = useTheme();
  const colors: Record<ActivityCountKey, string> = {
    activeUsers: theme.palette.text.primary,
    searches: theme.palette.secondary.main,
    compares: theme.palette.info.main,
    downloads: theme.palette.warning.dark,
    uploads: 'rgb(156, 39, 176)',
    reports: theme.palette.error.light,
  };
  const color = colors[metric];
  const axisColor = theme.palette.text.secondary;

  const maxValue = niceCeiling(Math.max(0, ...daily.map(day => day[metric])));
  const slotWidth = PLOT_WIDTH / Math.max(daily.length, 1);
  const x = (index: number) => MARGIN.left + (index + 0.5) * slotWidth;
  const y = (value: number) => MARGIN.top + PLOT_HEIGHT - (value / maxValue) * PLOT_HEIGHT;
  const points = daily.map((day, index) => `${x(index)},${y(day[metric])}`);
  const hoveredDay = hoveredIndex === undefined ? undefined : daily[hoveredIndex];
  const first = daily[0];
  const last = daily.at(-1);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width='100%'
      role='img'
      aria-label={`${label} per day`}
      data-testid={`activity-sparkline-${metric}`}
    >
      {[0, maxValue].map(value => (
        <g key={value}>
          <line x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={y(value)} y2={y(value)} stroke={theme.palette.divider} />
          <text x={MARGIN.left - 4} y={y(value) + 3} textAnchor='end' fontSize={AXIS_FONT_SIZE} fill={axisColor}>
            {value.toLocaleString()}
          </text>
        </g>
      ))}
      {points.length > 1 && (
        <polygon
          points={`${x(0)},${y(0)} ${points.join(' ')} ${x(daily.length - 1)},${y(0)}`}
          fill={color}
          opacity={0.12}
        />
      )}
      <polyline
        data-testid={`activity-line-${metric}`}
        fill='none'
        stroke={color}
        strokeWidth='2'
        strokeLinejoin='round'
        points={points.join(' ')}
      />
      {first && (
        <text x={x(0)} y={HEIGHT - 4} textAnchor='start' fontSize={AXIS_FONT_SIZE} fill={axisColor}>
          {formatDay(first.date)}
        </text>
      )}
      {last && last !== first && (
        <text x={x(daily.length - 1)} y={HEIGHT - 4} textAnchor='end' fontSize={AXIS_FONT_SIZE} fill={axisColor}>
          {formatDay(last.date)}
        </text>
      )}
      {daily.map((day, index) => (
        <rect
          key={day.date}
          x={x(index) - slotWidth / 2}
          y={MARGIN.top}
          width={slotWidth}
          height={PLOT_HEIGHT}
          fill='transparent'
          data-testid={`activity-day-${metric}-${day.date}`}
          onMouseOver={() => setHoveredIndex(index)}
          onMouseOut={() => setHoveredIndex(undefined)}
        />
      ))}
      {hoveredDay && (
        <Tooltip
          open
          title={`${formatDay(hoveredDay.date)}: ${hoveredDay[metric].toLocaleString()}`}
          placement='top'
          arrow
        >
          <rect
            x={x(hoveredIndex ?? 0) - slotWidth / 2}
            y={MARGIN.top}
            width={slotWidth}
            height={PLOT_HEIGHT}
            fill='transparent'
            pointerEvents='none'
          />
        </Tooltip>
      )}
    </svg>
  );
};
